import React from "react";
import { FiChevronDown, FiChevronRight, FiFileText, FiFolder, FiPackage } from "react-icons/fi";
import type { APAADMINBaseModelsPageTreeNode } from "../../API/Models";

type PageTreeRowProps = {
	node: APAADMINBaseModelsPageTreeNode;
	depth: number;
	variant: "folder" | "page";
	isExpanded?: boolean;
	isActive?: boolean;
	isRootPage?: boolean;
	onToggle?: () => void;
	onSelect?: () => void;
};

/** Individual tree row representing a folder or page with active indicator. */
const PageTreeRow: React.FC<PageTreeRowProps> = ({
	node,
	depth,
	variant,
	isExpanded = false,
	isActive = false,
	isRootPage = false,
	onToggle,
	onSelect
}) => {
	const paddingLeft = depth * 16 + 8;
	const label = isRootPage ? "/" : node.title || node.name || node.slug || node.path || "Untitled";
	const secondaryLabel = isRootPage
		? node.title || node.slug || node.path || null
		: node.slug
		? `/${node.slug}`
		: null;

	return (
		<div
			className={`group flex items-center gap-2 rounded-lg px-2 py-1.5 transition ${
				isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
			}`}
			style={{ paddingLeft }}
		>
			<div
				className={`flex h-6 w-6 items-center justify-center rounded-md border ${
					isActive ? "border-white/40 text-white/90" : "border-slate-200 text-slate-500"
				}`}
			>
				{variant === "folder" ? <FiFolder className="h-4 w-4" /> : <FiFileText className="h-4 w-4" />}
			</div>
			<button
				type="button"
				onClick={onSelect}
				className="flex min-w-0 flex-1 items-center gap-2 text-left"
			>
				<div className="min-w-0">
					<p className="flex items-center gap-1.5 text-sm font-semibold">
						<span className="truncate">{label}</span>
						{variant === "page" && node.source && (
							<FiPackage
								className={`h-3 w-3 shrink-0 ${isActive ? "text-white/80" : "text-[#FFA401]"}`}
								title={`Migrated page — source: ${node.source}`}
							/>
						)}
					</p>
					{variant === "page" && secondaryLabel && (
						<p className={`truncate text-[11px] ${isActive ? "text-white/70" : "text-slate-400"}`}>
							{secondaryLabel}
						</p>
					)}
				</div>
			</button>
			{variant === "folder" && onToggle ? (
				<button
					type="button"
					onClick={onToggle}
					className={`ml-auto flex h-6 w-6 items-center justify-center rounded-md border ${
						isActive
							? "border-white/40 text-white/90"
							: "border-slate-200 text-slate-500 group-hover:border-slate-300"
					}`}
					aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
				>
					{isExpanded ? <FiChevronDown className="h-4 w-4" /> : <FiChevronRight className="h-4 w-4" />}
				</button>
			) : (
				<div className="ml-auto h-6 w-6" />
			)}
		</div>
	);
};

export default PageTreeRow;
